// Kindly API service using the centralized API client
// This wraps the backend FastAPI endpoints

import { apiClient, fetchWithSession } from "./api";
import { fetchWithTimeout } from "@jkast90/kast-common/fetch";
import { apiErrorStatus } from "@jkast90/kast-common/api-client";
import { errorMessage } from "@jkast90/kast-common/format";

export { apiErrorStatus };

/** Photo uploads, which carry the file body. */
const UPLOAD_TIMEOUT_MS = 30000;

// Current user ID (would come from auth in production)
export const CURRENT_USER_ID = 1; // Jeremy Kast

// Type definitions matching backend schemas

/** A picture in the family's shared pool: the profile stores this reference, never a copy. */
export interface PoolPicture {
  kind: "pool";
  id: string;
  url: string;
}

/** What to show for a person: their pool picture, else an old photo that is already a URL (a stored key needs signing: `usePhotoUrl`). */
export const pictureSrc = (
  person: Pick<Profile, "picture" | "profile_picture"> | null | undefined,
): string | null =>
  person?.picture?.url ??
  (person?.profile_picture?.startsWith("http") ? person.profile_picture : null);
export interface Profile {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  bio?: string;
  pronouns?: string;
  /** An old own-bucket photo (a key or URL), shown until a pool picture is picked. */
  profile_picture?: string;
  /** The picture picked from the family's pool, by reference. */
  picture?: PoolPicture | null;
  pants_size?: string;
  shirt_size?: string;
  color?: string;
  birthday?: string;
  profile_type?: string;
}

/**
 * A new profile. One's own needs nothing more; one for someone else (a
 * child, a relative, a pet, a memorial) names `family_id`, a family the
 * caller heads, and joins it as it is made.
 */
export type ProfileCreate = Omit<Profile, "id"> & { family_id?: number };

const messageOf = (error: unknown): string => errorMessage(error, "");

/** Whether an API error is a 404: missing, or not yours to see (the API answers both alike). */
export const isNotFoundError = (error: unknown): boolean =>
  apiErrorStatus(error) === 404 || /\bnot found\b/i.test(messageOf(error));

export interface WishlistItem {
  id: number;
  owner: string;
  wishee_id: number;
  description: string;
  link?: string;
  price?: number;
  priority: string;
  category?: string;
  claim_status: string;
  claimed_by?: string | null;
  reveal_date?: string | null;
  size?: string | null;
  quantity?: number;
}

export interface WishlistData {
  wishlist_items: WishlistItem[];
  wishers: Profile[];
}

export interface MemoryComment {
  id: number;
  memory_id: number;
  user_id: number;
  user_name: string;
  comment: string;
  created_at: string;
}

export interface Memory {
  id: number;
  title: string;
  description: string;
  date?: string;
  created_by: number;
  created_at: string;
  tagged_users: number[];
  suggestions?: string;
  tags: string[];
  images: string[];
  comments: MemoryComment[];
}

export interface ImprintComment {
  id: number;
  imprint_id: number;
  user_id: number;
  user_name: string;
  comment: string;
  created_at: string;
}

export interface Imprint {
  id: number;
  title: string;
  description: string;
  category: string; // song, place, food, quote, object, activity, other
  created_by: number;
  created_at: string;
  tagged_users: number[];
  tags: string[];
  images: string[];
  comments: ImprintComment[];
}

export interface ThankYou {
  id: number;
  from_user_id: number;
  from_user_name: string;
  to_user_id: number;
  to_user_name: string;
  gift_description: string;
  message: string;
  created_at: string;
}

export interface CalendarEvent {
  id: number;
  title: string;
  date: string;
  type: string;
  description?: string;
  location?: string;
  user_id?: number | null;
  tags: string[];
  memory_ids: number[];
  invited_user_ids: number[];
}

export interface FeedActivity {
  id: number;
  type: "claim" | "memory" | "comment" | "wishlist" | "thank_you" | "imprint";
  user_id: number;
  user_name: string;
  timestamp: string;
  action: string;
  item_description?: string;
  wishee_id?: number;
  wishee_name?: string;
  memory_id?: number;
  memory_title?: string;
  imprint_id?: number;
  imprint_title?: string;
  comment?: string;
  to_user_id?: number;
  to_user_name?: string;
  gift_description?: string;
  message?: string;
}

// Use the centralized API client with all its logging capabilities
const kindlyClient = apiClient;

// Profile API
export const profilesApi = {
  getAll: (userId?: number) => {
    const params = userId ? `?user_id=${userId}` : "";
    return kindlyClient.get<Profile[]>(`/profiles${params}`);
  },
  getById: (id: number) => kindlyClient.get<Profile>(`/profiles/${id}`),
  getByEmail: (email: string) =>
    kindlyClient.get<Profile>(`/profiles?email=${encodeURIComponent(email)}`),
  create: (profile: ProfileCreate) =>
    kindlyClient.post<Profile>("/profiles", profile),
  update: (id: number, profile: Partial<Profile>) =>
    kindlyClient.put<Profile>(`/profiles/${id}`, profile),
  delete: (id: number) => kindlyClient.delete<void>(`/profiles/${id}`),
};

// User API
export const usersApi = {
  sendVerificationEmail: () =>
    kindlyClient.post<{ message: string }>("/users/me/verify-email", {}),
  getAllForFamilies: () => kindlyClient.get<Profile[]>("/families/users"),
};

// Wishlist API
export const wishlistApi = {
  getAll: () => kindlyClient.get<WishlistData>("/wishlist"),
  create: (
    item: Omit<WishlistItem, "id" | "owner" | "claim_status" | "claimed_by">,
  ) => kindlyClient.post<WishlistItem>("/wishlist_items", item),
  update: (id: number, item: Partial<WishlistItem>) =>
    kindlyClient.put<WishlistItem>(`/wishlist_items/${id}`, item),
  delete: (id: number) => kindlyClient.delete<void>(`/wishlist_items/${id}`),
  claim: (id: number, claimedBy: string, revealDate?: string) =>
    kindlyClient.put<WishlistItem>(`/wishlist_items/${id}/claim`, {
      claimed_by: claimedBy,
      reveal_date: revealDate,
    }),
  unclaim: (id: number) =>
    kindlyClient.delete<WishlistItem>(`/wishlist_items/${id}/claim`),
  move: (id: number, newWisheeId: number) =>
    kindlyClient.put<WishlistItem>(
      `/wishlist_items/${id}/move/${newWisheeId}`,
      {},
    ),
  getClaimedGifts: (profileId: number) =>
    kindlyClient.get<WishlistItem[]>(`/profiles/${profileId}/claimed_gifts`),
};

// Memories API
export const memoriesApi = {
  getAll: () => kindlyClient.get<Memory[]>("/memories"),
  getById: (id: number) => kindlyClient.get<Memory>(`/memories/${id}`),
  create: (memory: Omit<Memory, "id" | "created_at" | "comments">) =>
    kindlyClient.post<Memory>("/memories", memory),
  update: (id: number, memory: Partial<Memory>) =>
    kindlyClient.put<Memory>(`/memories/${id}`, memory),
  delete: (id: number) => kindlyClient.delete<void>(`/memories/${id}`),
  addComment: (
    memoryId: number,
    comment: Omit<MemoryComment, "id" | "memory_id" | "created_at">,
  ) =>
    kindlyClient.post<MemoryComment>(`/memories/${memoryId}/comments`, comment),
};

// Imprints API
export const imprintsApi = {
  getAll: () => kindlyClient.get<Imprint[]>("/imprints"),
  getById: (id: number) => kindlyClient.get<Imprint>(`/imprints/${id}`),
  create: (imprint: Omit<Imprint, "id" | "created_at" | "comments">) =>
    kindlyClient.post<Imprint>("/imprints", imprint),
  update: (id: number, imprint: Partial<Imprint>) =>
    kindlyClient.put<Imprint>(`/imprints/${id}`, imprint),
  delete: (id: number) => kindlyClient.delete<void>(`/imprints/${id}`),
  addComment: (
    imprintId: number,
    comment: Omit<ImprintComment, "id" | "imprint_id" | "created_at">,
  ) =>
    kindlyClient.post<ImprintComment>(
      `/imprints/${imprintId}/comments`,
      comment,
    ),
};

// Thank You Notes API
export const thankYousApi = {
  getAll: () => kindlyClient.get<ThankYou[]>("/thank_yous"),
  create: (
    thankYou: { to_user_id: number; gift_description: string; message: string },
    currentUserId: number,
  ) =>
    kindlyClient.post<ThankYou>(
      `/thank_yous?current_user_id=${currentUserId}`,
      thankYou,
    ),
  getSent: (profileId: number) =>
    kindlyClient.get<ThankYou[]>(`/profiles/${profileId}/thank_yous/sent`),
  getReceived: (profileId: number) =>
    kindlyClient.get<ThankYou[]>(`/profiles/${profileId}/thank_yous/received`),
  delete: (thankYouId: number) =>
    kindlyClient.delete(`/thank_yous/${thankYouId}`),
};

// Calendar API
export const calendarApi = {
  getAll: () => kindlyClient.get<CalendarEvent[]>("/calendar"),
  getById: (id: number) => kindlyClient.get<CalendarEvent>(`/calendar/${id}`),
  create: (event: Omit<CalendarEvent, "id">) =>
    kindlyClient.post<CalendarEvent>("/calendar", event),
  update: (id: number, event: Partial<CalendarEvent>) =>
    kindlyClient.put<CalendarEvent>(`/calendar/${id}`, event),
  delete: (id: number) => kindlyClient.delete<void>(`/calendar/${id}`),
};

// Feed API
export const feedApi = {
  getAll: (limit?: number, excludeUserId?: number) => {
    const params = new URLSearchParams();
    if (limit) params.append("limit", limit.toString());
    if (excludeUserId)
      params.append("exclude_user_id", excludeUserId.toString());
    const query = params.toString() ? `?${params.toString()}` : "";
    return kindlyClient.get<FeedActivity[]>(`/feed${query}`);
  },
  create: (activity: Omit<FeedActivity, "id" | "timestamp">) =>
    kindlyClient.post<FeedActivity>("/feed", activity),
  delete: (id: number) => kindlyClient.delete<void>(`/feed/${id}`),
};

// Holidays API
export const holidaysApi = {
  generateForYear: (year: number) =>
    kindlyClient.post<{
      year: number;
      added_count: number;
      skipped_count: number;
      total_holidays: number;
    }>(`/holidays/generate/${year}`, {}),
};

// Yearly Summary API
export interface YearlySummary {
  year: number;
  users: string[];
  summary: string;
  memories_count: number;
  events_count: number;
  generated_at: string;
}

export const yearlySummaryApi = {
  generate: (
    year: number,
    userIds?: number[],
    occasion?: string,
    timePeriod?: string,
  ) => {
    const body: {
      user_ids?: number[];
      occasion?: string;
      time_period?: string;
    } = {};
    if (userIds) body.user_ids = userIds;
    if (occasion) body.occasion = occasion;
    if (timePeriod) body.time_period = timePeriod;
    return kindlyClient.post<YearlySummary>(`/yearly-summary/${year}`, body);
  },
};

// Prompts API
export interface PromptsResponse {
  prompts: string[];
}

export const promptsApi = {
  generatePrompts: (
    contentType: "memory" | "imprint",
    title?: string,
    description?: string,
    category?: string,
  ) => {
    const body: {
      content_type: "memory" | "imprint";
      title?: string;
      description?: string;
      category?: string;
    } = {
      content_type: contentType,
    };
    if (title) body.title = title;
    if (description) body.description = description;
    if (category) body.category = category;
    return kindlyClient.post<PromptsResponse>("/generate-prompts", body);
  },
};

// Nicknames API
export interface Nickname {
  id: number;
  user_id: number;
  assigned_by_id: number;
  nickname: string;
  /** How it got the name; none when left blank. */
  story?: string | null;
  created_at: string;
  updated_at: string;
}

export const nicknamesApi = {
  getForUser: (userId: number) =>
    kindlyClient.get<Nickname[]>(`/profiles/${userId}/nicknames`),
  getGiven: (assignedById: number) =>
    kindlyClient.get<Nickname[]>(
      `/nicknames/given?assigned_by_id=${assignedById}`,
    ),
  create: (
    userId: number,
    nickname: string,
    assignedById: number,
    story?: string,
  ) =>
    kindlyClient.post<Nickname>(`/nicknames?assigned_by_id=${assignedById}`, {
      user_id: userId,
      nickname,
      story,
    }),
  /** `story` left out keeps the story; blank clears it. */
  update: (
    nicknameId: number,
    nickname: string,
    assignedById: number,
    story?: string,
  ) =>
    kindlyClient.put<Nickname>(
      `/nicknames/${nicknameId}?assigned_by_id=${assignedById}`,
      { nickname, story },
    ),
  delete: (nicknameId: number, assignedById: number) =>
    kindlyClient.delete(
      `/nicknames/${nicknameId}?assigned_by_id=${assignedById}`,
    ),
};

// Photos API
export interface PhotoData {
  s3_key: string;
  url: string;
  filename: string;
  content_type: string;
  file_size: number;
}

export interface MemoryPhoto {
  id: number;
  memory_id: number;
  s3_key: string;
  filename: string;
  content_type: string;
  file_size: number;
  uploaded_by: number;
  uploaded_at: string;
  display_order: number;
}

export const photosApi = {
  upload: async (
    file: File,
    folder: "memories" | "profiles" | "imprints" | "uploads" = "uploads",
  ): Promise<PhotoData> => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("folder", folder);

    // Make direct fetch call to bypass apiClient's JSON handling
    // In production, CloudFront handles HTTPS on standard port 443
    // In development, API runs on port 8000 (HTTP)
    const baseUrl =
      window.location.protocol === "https:"
        ? `${window.location.protocol}//${window.location.hostname}/api`
        : `${window.location.protocol}//${window.location.hostname}:8000/api`;

    const url = `${baseUrl}/photos/upload`;

    const response = await fetchWithSession((auth) =>
      fetchWithTimeout(
        url,
        {
          method: "POST",
          // No Content-Type: the browser sets it, with the FormData boundary.
          headers: auth,
          body: formData,
        },
        {
          timeoutMs: UPLOAD_TIMEOUT_MS,
          message: "Upload timed out. Please try a smaller file.",
        },
      ),
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Upload failed: ${response.status} ${errorText}`);
    }

    const data = await response.json();
    return data;
  },
  addToMemory: async (
    memoryId: number,
    s3Key: string,
  ): Promise<{ message: string; images: string[] }> => {
    const formData = new FormData();
    formData.append("s3_key", s3Key);

    const baseUrl =
      window.location.protocol === "https:"
        ? `${window.location.protocol}//${window.location.hostname}/api`
        : `${window.location.protocol}//${window.location.hostname}:8000/api`;

    const response = await fetchWithSession((auth) =>
      fetchWithTimeout(`${baseUrl}/photos/memory/${memoryId}/add`, {
        method: "POST",
        headers: auth,
        body: formData,
      }),
    );

    if (!response.ok) {
      throw new Error(`Failed to add photo to memory: ${response.status}`);
    }

    return await response.json();
  },
  addToImprint: async (
    imprintId: number,
    s3Key: string,
  ): Promise<{ message: string; images: string[] }> => {
    const formData = new FormData();
    formData.append("s3_key", s3Key);

    const baseUrl =
      window.location.protocol === "https:"
        ? `${window.location.protocol}//${window.location.hostname}/api`
        : `${window.location.protocol}//${window.location.hostname}:8000/api`;

    const response = await fetchWithSession((auth) =>
      fetchWithTimeout(`${baseUrl}/photos/imprint/${imprintId}/add`, {
        method: "POST",
        headers: auth,
        body: formData,
      }),
    );

    if (!response.ok) {
      throw new Error(`Failed to add photo to imprint: ${response.status}`);
    }

    return await response.json();
  },
  createMemoryPhoto: (
    photoData: Omit<MemoryPhoto, "id" | "uploaded_at" | "uploaded_by">,
  ) => kindlyClient.post<MemoryPhoto>("/photos/memory", photoData),
  getMemoryPhotos: (memoryId: number) =>
    kindlyClient.get<MemoryPhoto[]>(`/photos/memory/${memoryId}`),
  getPhotoUrl: (photoId: number, expiration: number = 3600) =>
    kindlyClient.get<{ url: string }>(
      `/photos/${photoId}/url?expiration=${expiration}`,
    ),
  getS3Url: (s3Key: string, expiration: number = 3600) =>
    kindlyClient.get<{ url: string }>(
      `/photos/s3-url?s3_key=${encodeURIComponent(s3Key)}&expiration=${expiration}`,
    ),
  delete: (photoId: number) => kindlyClient.delete(`/photos/${photoId}`),
};

// Families API
export interface Family {
  id: number;
  name: string;
  description?: string;
  created_at: string;
  updated_at: string;
}

/** A row of GET /families/{id}/members (api-rs schemas/families.rs). */
export interface FamilyMember {
  user_id: number;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  head_of_household: boolean;
  joined_at: string;
}

/** A row of GET /families/{id}/available-users: someone the head can add. */
export interface FamilyUser {
  id: number;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
}

export interface FamilyInvitation {
  id: number;
  family_id: number;
  invited_user_id: number;
  invited_by_id: number;
  status: "pending" | "accepted" | "declined";
  created_at: string;
  updated_at: string;
}

/** GET /family-invitations: the invitee cannot open the family yet, so it is named here. */
export interface PendingInvitation extends FamilyInvitation {
  family_name: string;
  invited_by_name: string;
}

export const familiesApi = {
  getAll: () => kindlyClient.get<Family[]>("/families"),
  getById: (familyId: number) =>
    kindlyClient.get<Family>(`/families/${familyId}`),
  getMembers: (familyId: number) =>
    kindlyClient.get<FamilyMember[]>(`/families/${familyId}/members`),
  create: (family: { name: string; description?: string }) =>
    kindlyClient.post<Family>("/families", family),
  update: (familyId: number, family: { name?: string; description?: string }) =>
    kindlyClient.put<Family>(`/families/${familyId}`, family),
  delete: (familyId: number) => kindlyClient.delete(`/families/${familyId}`),
  addMember: (familyId: number, memberUserId: number) =>
    kindlyClient.post(`/families/${familyId}/members`, {
      member_user_id: memberUserId,
    }),
  removeMember: (familyId: number, memberUserId: number) =>
    kindlyClient.delete(`/families/${familyId}/members/${memberUserId}`),
  /** People the head can add directly: those they look after in their other families. */
  getAvailableUsers: (familyId: number) =>
    kindlyClient.get<FamilyUser[]>(`/families/${familyId}/available-users`),
  /** Invite someone with a kindly profile; they join when they accept. */
  inviteByEmail: (familyId: number, email: string) =>
    kindlyClient.post<FamilyInvitation>(`/families/${familyId}/invitations`, {
      invited_email: email,
    }),
  getMyInvitations: () =>
    kindlyClient.get<PendingInvitation[]>("/family-invitations"),
  respondToInvitation: (
    invitationId: number,
    status: "accepted" | "declined",
  ) =>
    kindlyClient.put<FamilyInvitation>(`/family-invitations/${invitationId}`, {
      status,
    }),
};

// Reaction types
export type TargetType =
  "memory" | "memory_comment" | "imprint" | "imprint_comment";

export interface ReactionSummary {
  emoji: string;
  count: number;
  user_ids: number[];
}

export interface ReactionsByTarget {
  target_type: TargetType;
  target_id: number;
  reactions: ReactionSummary[];
}

export const reactionsApi = {
  get: (targetType: TargetType, targetId: number) =>
    kindlyClient.get<ReactionsByTarget>(`/reactions/${targetType}/${targetId}`),
  getBatch: (targetType: TargetType, targetIds: number[]) =>
    kindlyClient.get<ReactionsByTarget[]>(
      `/reactions/batch/${targetType}?target_ids=${targetIds.join(",")}`,
    ),
  add: (targetType: TargetType, targetId: number, emoji: string) =>
    kindlyClient.post(`/reactions/${targetType}/${targetId}`, { emoji }),
  remove: (targetType: TargetType, targetId: number, emoji: string) =>
    kindlyClient.delete(
      `/reactions/${targetType}/${targetId}/${encodeURIComponent(emoji)}`,
    ),
};
